import UpdateSubcribeIcon from "icons/dashboard/UpdateSubcribeIcon";
import BreadCrumbs from "components/ui-component/BreadCrumbs";
import {
  getGeneralDetailMarkers,
  getOperatingSystem,
  getProductFrequency,
} from "utils/HelperFunctions";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { productsDueAmount, updatePlan } from "store/slices/subscriptionSlice";
import {
  paymentUpdateSubscriptionData,
  useLeadData,
  usePortalSettingsData,
} from "store/slices/dashboardSlice";
import {
  addNotification,
  updateUserData,
  useUserData,
} from "store/slices/authSlice";
import {
  createPaypalPaymentToken,
  oneTimeMakePayment,
  resetCheckDuplicatePayment,
  resetDuplicateOrder,
  updateLogoAndFavicon,
  updatePaymentPermission,
  verifyWalletPay,
} from "store/slices/paymentsSlice";
import DisclosureInitial from "components/makePayment/DisclosureInitial";
import { decode } from "utils/HelperFunctions";
import {
  onSetFinalDisclosureLoading,
  onSetShowDisclosure,
  useShowFinalDisclosure,
} from "store/slices/commonSlice";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { FormRef, SettingsValue } from "types/payments";
import DisclosureFinal from "components/makePayment/DisclosureFinal";
import CustomModal from "components/ui-component/CustomModal";
import PlanSelection from "./PlanSelection";
import UpdateSubscribePreview from "./UpdateSubscribePreview";
import PaymentStepContent from "./PaymentStepContent";
import {
  UpdateSusbcribePlanProps,
  StepConfig,
  SelectedProduct,
  SubscriptionData,
  CurrentProduct,
  PlanFilter,
  PlanFrequency,
} from "types/subscriptionAndPlans";
import { RootState } from "store/store";
import { CloseIcon } from "icons/close/CloseIcon";

const UpdateSusbcribePlan = (props: UpdateSusbcribePlanProps) => {
  const {
    id,
    show,
    setShow,
    setId,
    fetchDetails,
    setHandleDrawerSize,
    setIsPreview = () => {},
    setIsPreviewId,
    errrorMessage,
    setErrorMessage,
  } = props;
  const portalSettings = usePortalSettingsData();
  const showFinalDisclosure = useShowFinalDisclosure();
  const updateRef = useRef<FormRef>(null);
  const confirmButtonRef = useRef<FormRef>(null);

  const [initialDisclosureAccept, setInitialDisclosureAccept] =
    useState<boolean>(false);
  const [planOptions, setPlanOptions] = useState<Record<string, any[]>>({});
  const [planFrequency, setPlanFrequency] = useState<PlanFrequency[]>([
    {
      name: "Monthly",
      value: "month",
      interval_custom_count: 1,
      interval_custom_selector: "month",
    },
  ]);
  const [missingCurrentItems, setMissingCurrentItems] = useState<
    CurrentProduct[]
  >([]);
  const [missingPlanFrequency, setMissingPlanFrequency] = useState<
    PlanFrequency[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [paymentSuccess, setPaymentSuccess] = useState<boolean>(false);
  const [screen, setScreen] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const userData = useUserData();
  const accountLeadData = useLeadData();

  const dispatch = useAppDispatch();

  const steps: StepConfig[] = [
    {
      id: 0,
      title: "Subscriptions & Plans",
    },
    {
      id: 1,
      title: "Plans",
    },
    {
      id: 2,
      title: "Update plan",
    },
    {
      id: 3,
      title: paymentSuccess ? "Confirm" : "Payment method",
    },
  ];

  const [selectedPlan, setSelectedPlan] = useState<PlanFilter>({
    name: "Monthly",
    value: "month",
    interval_count: 1,
    interval_selector: "month",
    bottomDisplay: "per month",
  });

  const [products, setProducts] = useState<SelectedProduct[]>([]);
  const [currentProducts, setCurrentProducts] = useState<CurrentProduct[]>([]);
  const [currentPlan, setCurrentPlan] = useState<SubscriptionData>(
    {} as SubscriptionData
  );
  const [amountDue, setAmountDue] = useState(0);
  const [startDate, setStartDate] = useState(0);
  const [proratedCharge, setProratedCharge] = useState(0);
  const [proratedCredit, setProratedCredit] = useState(0);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isScreenLoading, setIsScreenLoading] = useState<boolean>(false);
  const [isManualForm, setIsManualForm] = useState<boolean>(true);
  const [activeSubtotalFrequency, setActiveSubtotalFrequency] =
    useState<PlanFilter>({
      name: "Monthly",
      value: "month",
      interval_count: 1,
      interval_selector: "month",
      bottomDisplay: "per month",
    });
  const [dismissedCurrentPids, setDismissedCurrentPids] = useState<string[]>(
    []
  );

  // Subtotal should follow the frequency of current selections, not the visible tab
  const missingCurrentTotalForSubtotal = useMemo(() => {
    if (isLoading) return 0;
    const freq = String(activeSubtotalFrequency?.value || "");
    if (!freq) return 0;
    const opts = planOptions[freq] || [];
    const items = (currentProducts || []).filter((cp: any) => {
      const cpFreq = String(cp?.schedule_key || "");
      const cpPid = String(cp?.pid ?? "");
      const existsInOpts = opts.some(
        (po: any) => String(po?.id ?? "") === cpPid
      );
      return cpFreq === freq && !existsInOpts;
    });
    return items.reduce((sum: number, cp: any) => {
      const cpPid = String(cp?.pid ?? "");
      if (dismissedCurrentPids.includes(cpPid)) return sum;
      const qty = cp?.quantity || 1;
      const amt = parseFloat(cp?.amount) || 0;
      return sum + amt * qty;
    }, 0);
  }, [
    isLoading,
    activeSubtotalFrequency,
    currentProducts,
    planOptions,
    dismissedCurrentPids,
  ]);

  const { payPalOrder, conFee, isDuplicateOrders, duplicateOrdersCount } =
    useAppSelector((state: RootState) => state.payment);
  const { planDetails } = useAppSelector(
    (state: RootState) => state.subscription
  );

  const handleFormatFreqAndOpt = async ({
    customerData,
    paymentPermission,
    products,
    companyLogoDetails,
  }: any) => {
    dispatch(updatePaymentPermission(paymentPermission));
    dispatch(
      updateUserData({
        ...userData?.data?.accountData,
        ...customerData,
      })
    );
    dispatch(updateLogoAndFavicon(companyLogoDetails));
    let obj: any = {};
    let frequencies = [];
    for (let i = 0; i < products?.length; i++) {
      let item = products[i];
      if (item && item.productPrices) {
        let { name, description, product_id, productPrices } = item;
        for (let j = 0; j < productPrices.length; j++) {
          let product = productPrices[j];
          let schedule = product?.schedule || "day";
          let interval_count = product?.interval_custom_count || 1;
          let interval_selector = product?.interval_custom_selector || "month";
          let keys = getProductFrequency(
            schedule,
            interval_count,
            interval_selector
          );

          frequencies.push({ ...keys });

          if (!obj[keys.value]) {
            obj[keys.value] = [];
          }

          obj[keys.value].push({
            name,
            description,
            product_id,
            schedule: product?.schedule,
            price_id: product?.price_id,
            price: product?.price,
            pid: product?.pid,
            id: product?.id,
            schedule_key: keys?.value,
            interval_custom_count: product?.interval_custom_count,
            interval_custom_selector: product?.interval_custom_selector,
            image_name: item.image_name,
            image_path: item.image_path,
          });
        }
      }
    }

    return { frequencies, planOptions: obj };
  };

  const getPlanData = async () => {
    try {
      let payload = {
        slug: id,
      };
      let res: any = await dispatch(
        paymentUpdateSubscriptionData(payload)
      ).unwrap();
      if (res?.body?.success) {
        let { data } = res?.body;
        let { frequencies, planOptions } = await handleFormatFreqAndOpt(data);
        setPlanFrequency(frequencies);
        setPlanOptions(planOptions);

        let { subscriptionData } = data;

        let { productSubscriptions } = subscriptionData;
        let totalAmountCalc = 0;
        let missingItems: CurrentProduct[] = [];
        if (productSubscriptions?.length) {
          const schedule = productSubscriptions?.[0]?.priceDetails?.schedule;
          const interval_custom_count =
            productSubscriptions?.[0]?.priceDetails?.interval_custom_count;
          const interval_custom_selector =
            productSubscriptions?.[0]?.priceDetails?.interval_custom_selector;

          const freq = getProductFrequency(
            schedule,
            interval_custom_count,
            interval_custom_selector
          );

          let arr = [];

          for (let i = 0; i < productSubscriptions?.length; i++) {
            let products = productSubscriptions[i];

            if (products?.priceDetails?.frequency !== "onetime") {
              totalAmountCalc += products?.amount * products?.quantity;
              arr.push({
                id: products?.productDetails?.id,
                name: products?.productDetails?.name,
                amount: products?.amount,
                pid: products?.prod_price_id,
                frequency: freq?.value || "month",
                quantity: products?.quantity,
                image_name: products?.productDetails?.image_name,
                image_path: products?.productDetails?.image_path,
                schedule_key: freq?.value,
                interval_custom_count:
                  products?.priceDetails?.interval_custom_count || 1,
                interval_custom_selector:
                  products?.priceDetails?.interval_custom_selector || "month",
              });
            }
          }

          setCurrentProducts(arr);
          setProducts(arr);
          if (freq) {
            setSelectedPlan({
              name: freq?.name || "Monthly",
              value: freq?.value || "month",
              interval_count: interval_custom_count || 1,
              interval_selector: interval_custom_selector || "month",
              bottomDisplay: freq?.bottomDisplay || "per month",
            });
            setActiveSubtotalFrequency({
              name: freq?.name || "Monthly",
              value: freq?.value || "month",
              interval_count: interval_custom_count || 1,
              interval_selector: interval_custom_selector || "month",
              bottomDisplay: freq?.bottomDisplay || "per month",
            });

            if (!frequencies.some((f) => f.value === freq?.value)) {
              setMissingPlanFrequency((prev) => [...prev, freq]);
            } else {
              setMissingPlanFrequency([]);
            }

            const opts = planOptions[freq?.value] || [];
            missingItems =
              arr
                .filter((cp: any) => {
                  const cpPid = String(cp?.pid ?? "");
                  const existsInOpts = opts.some(
                    (po: any) => String(po?.id ?? "") === cpPid
                  );
                  return !existsInOpts;
                })
                .map((cp: any) => ({ ...cp, schedule_display: freq.value })) ||
              [];

            setMissingCurrentItems(missingItems);
          }
        }

        setCurrentPlan(subscriptionData);
        setTotalAmount(missingItems?.length > 0 ? 0 : totalAmountCalc);
      }
    } catch (error) {
      console.log("getPlanData error =====", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (show && id && !searchParams.has("subsDrawer")) {
      getPlanData();
    }
  }, [show, id]);

  const handleOnClick = (id: number) => {
    if (!paymentSuccess) {
      setCurrentStep(id);
      setPaymentSuccess(false);
      setScreen(false);
      dispatch(onSetShowDisclosure(false));
      dispatch(onSetFinalDisclosureLoading(false));
    }
  };

  const onClose = () => {
    setId("");
    setShow(false);
    setCurrentStep(1);
    setProducts([]);
    setCurrentProducts([]);
    setDismissedCurrentPids([]);
    setCurrentPlan({} as SubscriptionData);
    setSelectedPlan({
      name: "Monthly",
      value: "month",
      interval_count: 1,
      interval_selector: "month",
      bottomDisplay: "per month",
    });
    setActiveSubtotalFrequency({
      name: "Monthly",
      value: "month",
      interval_count: 1,
      interval_selector: "month",
      bottomDisplay: "per month",
    });
    setPaymentSuccess(false);
    setScreen(false);
    setIsLoading(true);
    setTotalAmount(0);
    setInitialDisclosureAccept(false);
    if (paymentSuccess) {
      fetchDetails();
    }
    dispatch(onSetShowDisclosure(false));
    dispatch(onSetFinalDisclosureLoading(false));
  };

  const changePlanTab = (item: PlanFilter) => {
    setSelectedPlan(item);
  };

  const handleChangePlanTab = (updatedProducts: SelectedProduct[]) => {
    if (updatedProducts.length !== 0) {
      let lastProduct = updatedProducts[updatedProducts.length - 1];
      let filteredPlan = planFrequency.filter(
        (plans: any) =>
          plans?.value?.toLowerCase() === lastProduct?.schedule_key
      )[0];

      if (filteredPlan) {
        setActiveSubtotalFrequency({
          name: filteredPlan?.name || "Monthly",
          value: filteredPlan?.value || "month",
          interval_count: lastProduct?.interval_custom_count || 1,
          bottomDisplay: filteredPlan?.bottomDisplay || "per month",
          interval_selector:
            lastProduct?.interval_custom_selector ||
            filteredPlan?.value ||
            "month",
        });
      }

      setTotalAmount(
        updatedProducts.reduce(
          (sum, product) => sum + product.amount * product.quantity,
          0
        )
      );
    } else {
      setTotalAmount(0);
    }
  };

  const validateFreqAndInterval = (products: SelectedProduct[]): boolean => {
    if (products.length === 0) return true;

    const schedule = products[0]?.frequency;
    const count = products[0]?.interval_custom_count;
    const selector = products[0]?.interval_custom_selector;

    const isSameFrequencySelected = products.every(
      (obj: any) => obj.frequency === schedule
    );

    const isSameCustomIntervalSelected = products.every((obj: any) => {
      if (obj.frequency === "custom" && schedule === "custom") {
        return (
          obj.interval_custom_count === count &&
          obj.interval_custom_selector === selector
        );
      }
      return true;
    });

    return isSameFrequencySelected && isSameCustomIntervalSelected;
  };

  const isSameCustomAndFreqInterval = useMemo(() => {
    return validateFreqAndInterval(products);
  }, [products]);

  const handleSelectPlan = (product: any, isSelected: boolean) => {
    if (!isSelected) {
      let arr = [
        // ...products,
        {
          name: product?.name,
          id: product?.pid,
          amount: product?.price,
          frequency: product?.schedule,
          pid: product?.id,
          quantity: 1,
          interval_custom_count: product?.interval_custom_count,
          interval_custom_selector: product?.interval_custom_selector,
          image_name: product?.image_name,
          image_path: product?.image_path,
          schedule_key: product?.schedule_key,
        },
      ];
      let updatedArr = [...arr];
      missingCurrentItems.forEach((item) => {
        toggleMissingCurrent(item);
      });

      setProducts(updatedArr);
      let flag = validateFreqAndInterval(updatedArr);
      if (flag) {
        handleChangePlanTab(updatedArr);
      }
    } else {
      let arr = [...products];
      let updateArr = arr.filter((item: any) => item.pid !== product.id);

      setProducts(updateArr);

      let flag = validateFreqAndInterval(updateArr);
      if (flag) {
        handleChangePlanTab(updateArr);
      }
    }
  };

  const getDueAmount = async () => {
    setIsFetching(true);
    try {
      const plan = (products || []).map((product: any) => ({
        qty: product?.quantity || 1,
        product_id: product?.id,
        price_id: product?.pid,
      }));
      let payload = {
        subscription_slug: id,
        plan,
        accounts_id: userData?.data?.accountData?.accounts_id,
        lead_id:
          accountLeadData && accountLeadData?.lead_id
            ? accountLeadData?.lead_id
            : 0,
      };
      let res: any = await dispatch(productsDueAmount(payload));
      if (res?.payload?.body?.success) {
        let { amountDue, proratedCharge, proratedCredit, startingOn } =
          res?.payload?.body?.data;
        setAmountDue(amountDue);
        setProratedCharge(proratedCharge);
        setProratedCredit(proratedCredit);
        setStartDate(startingOn);
      }
    } catch (error) {
      console.log("error =====", error);
    } finally {
      setIsFetching(false);
      setCurrentStep((state: number) => state + 1);
    }
  };

  const handlePayPal = async () => {
    setIsScreenLoading(true);
    searchParams.set("checked", "true");
    const tokendata = {
      token: payPalOrder?.token,
    };
    try {
      let resp: any = await dispatch(
        createPaypalPaymentToken(tokendata)
      ).unwrap();
      if (resp && resp?.body && resp?.body?.success) {
        let value = {
          subscription_id: payPalOrder?.currentPlan?.id,
          lead_id: payPalOrder?.currentPlan?.lead_id || "",
          cluster_id: payPalOrder?.currentPlan?.clusterid,
          balance: userData?.data?.accountData?.balance,
          agent: payPalOrder?.currentPlan?.lead_owner,
          x_payment_method: "PP",
          city: "",
          address1: "",
          address2: "",
          zipcode: "",
          state: "",
          country: "",
          first_name: userData?.data?.accountData?.first_name || "",
          last_name: userData?.data?.accountData?.last_name || "",
          accountid: userData?.data?.accountData?.accountid || "",
          amount: payPalOrder?.amount,
          email: userData?.data?.accountData?.email || "",
          amount_type: "amount",
          use_old_card: "",
          description: "Online Payment",
          payment_note: "Subscription",
          customer_device: getOperatingSystem(),
          card_type: "",
          old_cardnumber: "",
          old_cvv: "",
          old_expiry: "",
          old_4digit: "",
          old_type: "",
          old_zip: "",
          cvv: "",
          cexpiry: "",
          ccardnumber: "",
          choldername: "",
          aholdername: "",
          routing_number: "",
          account_number: "",
          bank_name: "",
          bank_address: "",
          lead_owner: payPalOrder?.currentPlan?.lead_owner,
          cc_token: resp?.body?.data?.cc_token || "",
          stripe_token: resp?.body?.data?.cc_token || "",
          cppid: resp?.body?.data?.cppid || "",
          cfc: 0,
          isPayForProduct: true,
        };
        setIsScreenLoading(true);
        let res: any = await dispatch(oneTimeMakePayment(value)).unwrap();
        if (res && res?.body?.success) {
          let newProducts = planDetails?.selectedProducts || [];
          try {
            let plan: any[] = [];
            newProducts.forEach((product: any) => {
              plan.push({
                qty: product?.quantity,
                product_id: product.id,
                price_id: product.pid,
              });
            });
            let payload = {
              subscription_slug: payPalOrder.id,
              plan,
              email: payPalOrder?.currentPlan?.email,
              x_payment_method: "PP",
              billing_city: "",
              account_number: "",
              routing_number: "",
              bank_address: "",
              bank_name: "",
              cc_4digit: "",
              cc_type: "",
              cc_name: "",
              cc_cvv: "",
              cc_expiry: "",
              cc_token: "",
              billing_state: "",
              billing_zip: "",
              billing_address2: "",
              billing_address1: "",
              amount: payPalOrder?.amount,
              cppid: resp?.body?.data?.cppid,
              transaction_id: "",
              accounts_id: userData?.data?.accountData?.accounts_id,
              accountid: userData?.data?.accountData?.accountid,
              lead_id: payPalOrder?.currentPlan?.lead_id,
              lead_owner: payPalOrder?.currentPlan?.lead_owner,
              order_id: res?.body?.data?.invoice_id,
            };
            dispatch(updatePlan(payload))
              .unwrap()
              .then((result: any) => {
                if (result?.body?.success) {
                  setPaymentSuccess(true);
                  setScreen(true);
                  setTimeout(() => setIsScreenLoading(false), 1000);
                } else {
                  setErrorMessage(result?.body?.data?.msg);
                  setPaymentSuccess(true);
                  setScreen(false);
                }
              });
          } catch (error) {
            console.log("onUpdatePlan error =====", error);
          }
        } else {
          setScreen(false);
          setPaymentSuccess(true);
        }
        setTimeout(() => setIsScreenLoading(false), 1000);
      }
    } catch (error) {
      console.log("handlePayPal error =====", error);
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
    }
  };

  const onUpdatePlan = async (value: any) => {
    setIsLoading(true);
    let newProducts = planDetails?.selectedProducts || [];
    try {
      let plan: any[] = [];
      newProducts.forEach((product: any) => {
        plan.push({
          qty: product?.quantity,
          product_id: product.id,
          price_id: product.pid,
        });
      });
      let { paymentDetails } = value;
      let payload = {
        subscription_slug: id,
        plan,
        email: paymentDetails?.email || "",
        x_payment_method: value?.payment_method,
        billing_city: paymentDetails?.billing_city || "",
        account_number: paymentDetails?.account_number || "",
        routing_number: paymentDetails?.routing_number || "",
        bank_address: "",
        bank_name: paymentDetails?.bank_name || "",
        cc_4digit: paymentDetails?.card_last4 || "",
        cc_type: paymentDetails?.cc_type,
        cc_name: "",
        cc_cvv: paymentDetails?.cc_cvv || "",
        cc_expiry: paymentDetails?.cc_expiry || "",
        cc_token: paymentDetails?.cc_token || "",
        cc_token2: paymentDetails?.cc_token2 || "",
        billing_state: paymentDetails?.billing_state || "",
        billing_zip: paymentDetails?.billing_zipcode || "",
        billing_address2: paymentDetails?.billing_address2 || "",
        billing_address1: paymentDetails?.billing_address1 || "",
        amount: value.amount,
        cppid: "",
        transaction_id: value.transaction_id,
        accounts_id: userData?.data?.accountData?.accounts_id,
        accountid: userData?.data?.accountData?.accountid,
        lead_id: value?.lead_id,
        lead_owner: userData?.data?.accountData?.lead_owner,
        order_id: value?.invoice_id,
      };
      let res: any = await dispatch(updatePlan(payload));
      if (res?.payload?.body?.success) {
        let subscription = decode(id || "")?.split("_");
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "subscription",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "modified",
            activity_type: "subscription_event",
            clusterid: subscription[1],
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: value.amount,
            subscription_id: subscription[0],
            comments: `Subscription for ${userData?.data?.accountData?.accountid} was updated`,
          })
        );
        setPaymentSuccess(true);
        setScreen(true);
      } else {
        setPaymentSuccess(true);
        setErrorMessage(res?.payload?.body?.data?.msg);
        setScreen(false);
      }
    } catch (error) {
      console.log("onUpdatePlan error =====", error);
    } finally {
      setIsScreenLoading(false);
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
    }
  };

  const handleCappAndBank = async (
    method: string,
    clusterId: string | null,
    lead_id: any,
    subsId: string | null
  ) => {
    let payment_method =
      method === "cashapp"
        ? "CAPP"
        : method === "card"
          ? "CC"
          : method === "amazonPay"
            ? "AMZP"
            : "ACH";

    try {
      let payload = {
        lead_id: lead_id,
        cluster_id: clusterId,
        isrecurring: "Y",
        x_payment_method: payment_method,
        amount: searchParams.get("amount") || 0,
        accountid: userData?.data?.accountData?.accountid || "",
        first_name: userData?.data?.accountData?.first_name || "",
        last_name: userData?.data?.accountData?.last_name || "",
        phone_number: userData?.data?.accountData.phone,
        module: "process_payment",
        transaction_id: searchParams.get("setup_intent"),
        isScheduleOnetime: true,
        schedule_id: subsId,
        conv_fee: conFee,
        description: "Online Payment",
        payment_note: "Subscription",
        isPayForProduct: true,
        custom_conv: "on",
      };
      let res: any = await dispatch(verifyWalletPay(payload)).unwrap();
      if (res?.body?.success && !res?.body?.data?.isDuplicate) {
        await onUpdatePlan({
          payment_method,
          ...payload,
          cc_4digit: res?.body?.data?.paymentDetails?.card_last4,
          cc_type: res?.body?.data?.paymentDetails?.cc_type,
          cc_cvv: res?.body?.data?.paymentDetails?.cc_cvv || "XXX",
          cc_expiry: res?.body?.data?.paymentDetails?.cc_expiry,
          cc_token: res?.body?.data?.paymentDetails?.cc_token,
          invoice_id: res?.body?.data?.invoice_id,
          paymentDetails: res?.body?.data?.paymentDetails,
        });
      } else {
        setPaymentSuccess(true);
        setScreen(false);
      }
    } catch (error) {
      setPaymentSuccess(true);
      setScreen(false);
      console.log("handleCapp error =====", error);
    } finally {
      setIsScreenLoading(false);
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
    }
  };

  const handleReset = async () => {
    setIsScreenLoading(true);
    setInitialDisclosureAccept(true);
    dispatch(onSetShowDisclosure(false));
    dispatch(onSetFinalDisclosureLoading(false));
    setCurrentStep(3);
  };

  useEffect(() => {
    if (searchParams.has("subsDrawer") && id) {
      if (
        searchParams.has("method") &&
        !searchParams.has("checked") &&
        searchParams.has("clusterid")
      ) {
        handleReset();
        searchParams.set("checked", "true");
        if (
          searchParams.get("method") === "cashapp" ||
          searchParams.get("method") === "bank" ||
          searchParams.get("method") === "card" ||
          searchParams.get("method") === "amazonPay"
        ) {
          setIsScreenLoading(true);
          handleCappAndBank(
            searchParams.get("method") || "cashapp",
            searchParams.get("clusterid"),
            searchParams.get("lead_id"),
            searchParams.get("subsId")
          );
        }
        setTimeout(() => {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
          navigate({ search: "" });
        }, 500);
      }
    }
  }, [id]);

  useEffect(() => {
    if (
      (searchParams.has("approval_session_id") ||
        searchParams.has("approval_token_id")) &&
      !searchParams.has("checked") &&
      searchParams.has("status") &&
      searchParams.has("amount") &&
      searchParams.get("method") === "paypal" &&
      searchParams.get("subsDrawer") === "true"
    ) {
      handleReset();
      searchParams.set("checked", "true");
      handlePayPal();
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    }
  }, [id]);

  const generalMarkers = getGeneralDetailMarkers({
    conFee,
    paymentAmount: String(totalAmount || 0),
  });

  const handleAccept = async () => {
    await dispatch(onSetFinalDisclosureLoading(true));
    try {
      if (currentStep === 2) {
        if (confirmButtonRef.current) {
          confirmButtonRef.current.callHandleClick();
        }
      } else {
        if (updateRef.current) {
          updateRef.current.callHandleClick();
        }
      }
    } catch (error) {
      console.log("handleAccept error =====", error);
    }
  };

  const toggleMissingCurrent = (cp: any) => {
    const pid = String(cp?.pid ?? "");
    setDismissedCurrentPids((prev) =>
      prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]
    );
  };

  const handleUpdateQuantity = (id: any, value?: number) => {
    let copyArr = [...products];
    let updatedArr = copyArr.map((obj: any) => {
      if (obj.pid === id) {
        return { ...obj, quantity: value };
      } else {
        return { ...obj };
      }
    });
    setProducts(updatedArr);
    setTotalAmount(
      updatedArr.reduce((sum, item) => {
        return sum + item?.amount * (item?.quantity || 1);
      }, 0)
    );
  };

  return (
    <>
      <div
        className={`relative w-full bg-white h-full overflow-y-auto  lg:pt-10 pt-5 `}
      >
        <div className="flex items-center justify-between w-full lg:pb-[30px] pb-3 lg:px-10 px-5">
          <div className="flex items-center gap-2">
            {currentStep === 1 ? (
              <>
                <UpdateSubcribeIcon width="24" height="24" />
                <div className="lg:text-[26px] text-lg text-txtPrimary font-medium lg:leading-[30px] leading-[23.44px]">
                  Update Plan
                </div>
              </>
            ) : currentStep === 2 ? (
              <div className="lg:text-[26px] text-lg text-txtPrimary font-medium lg:leading-[30px] leading-[23.44px]">
                Confirm your updates
              </div>
            ) : (
              <div className="lg:text-[26px] text-lg text-txtPrimary font-medium lg:leading-[30px] leading-[23.44px]">
                {paymentSuccess ? "Confirm your updates" : "Payment method"}
              </div>
            )}
          </div>
          <span
            onClick={() => onClose()}
            className="cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
          >
            <CloseIcon size={30} />
          </span>
        </div>
        <div className="w-full lg:py-5 py-2 lg:px-10 px-5 ">
          <BreadCrumbs
            steps={steps}
            currentStep={currentStep}
            handleOnClick={handleOnClick}
          />
        </div>
        {portalSettings?.state_disclosure_flag === SettingsValue.OFF ||
        initialDisclosureAccept ? (
          <>
            <div className={showFinalDisclosure ? "hidden none" : ""}>
              {currentStep === 1 && (
                <PlanSelection
                  isLoading={isLoading}
                  selectedPlan={selectedPlan}
                  planOptions={planOptions}
                  products={products}
                  currentProducts={currentProducts}
                  totalAmount={totalAmount}
                  isFetching={isFetching}
                  dismissedCurrentPids={dismissedCurrentPids}
                  activeSubtotalFrequency={activeSubtotalFrequency}
                  missingCurrentItems={missingCurrentItems}
                  missingCurrentTotalForSubtotal={
                    missingCurrentTotalForSubtotal
                  }
                  onChangePlanTab={changePlanTab}
                  onSelectPlan={handleSelectPlan}
                  onGetDueAmount={getDueAmount}
                  onUpdateQuantity={(id, value) =>
                    handleUpdateQuantity(id, value)
                  }
                  isSameCustomAndFreqInterval={isSameCustomAndFreqInterval}
                  onToggleMissingCurrent={toggleMissingCurrent}
                  planFrequency={planFrequency}
                  missingPlanFrequency={missingPlanFrequency}
                />
              )}

              {currentStep === 2 && (
                <UpdateSubscribePreview
                  setCurrentStep={setCurrentStep}
                  newProducts={products}
                  currentPlan={currentPlan}
                  setPaymentProcess={setPaymentSuccess}
                  setScreen={setScreen}
                  grandTotal={totalAmount}
                  amountDue={amountDue}
                  proratedCharge={proratedCharge}
                  proratedCredit={proratedCredit}
                  startDate={startDate}
                  id={id}
                  ref={confirmButtonRef}
                  errrorMessage={errrorMessage}
                  setErrorMessage={setErrorMessage}
                />
              )}

              {currentStep === 3 && (
                <PaymentStepContent
                  currentStep={currentStep}
                  paymentSuccess={paymentSuccess}
                  screen={screen}
                  isScreenLoading={isScreenLoading}
                  errrorMessage={errrorMessage}
                  id={id}
                  newProducts={products}
                  currentPlan={currentPlan}
                  amount={amountDue}
                  isManualForm={isManualForm}
                  setHandleDrawerSize={setHandleDrawerSize}
                  setIsPreview={setIsPreview}
                  setIsPreviewId={setIsPreviewId}
                  onClose={onClose}
                  confirmButtonRef={confirmButtonRef}
                  updateRef={updateRef}
                  setIsManualForm={setIsManualForm}
                  setPaymentSuccess={setPaymentSuccess}
                  setCurrentStep={setCurrentStep}
                  setScreen={setScreen}
                />
              )}
            </div>
            {showFinalDisclosure && (
              <div className="h-[85%] lg:px-10 px-5">
                <DisclosureFinal
                  generalMarkers={generalMarkers}
                  handleAccept={handleAccept}
                  isDrawer={true}
                  isOpen={showFinalDisclosure}
                />
              </div>
            )}
          </>
        ) : (
          <div className="h-[85%] lg:px-10 px-5">
            <DisclosureInitial
              isOpen={true}
              handleAccept={() => setInitialDisclosureAccept(true)}
              generalMarkers={generalMarkers}
            />
          </div>
        )}
      </div>
      <CustomModal
        signModal={false}
        isLoading={false}
        isOpen={isDuplicateOrders && duplicateOrdersCount > 0}
        title="Alert"
        doneText="Complete Payment"
        className="max-w-[600px] w-full h-fit rounded-[10px]"
        cancleText="Cancel"
        onDoneClick={() => {
          dispatch(resetDuplicateOrder());
          handleAccept();
        }}
        onCloseClick={() => {
          dispatch(resetCheckDuplicatePayment());
          dispatch(resetDuplicateOrder());
        }}
        children={
          <React.Fragment>
            <div className="pt-5 lg:text-xl text-txtPrimary lg:leading-[30px] leading-[24px]">
              A similar payment was made in the last 10 minutes. Click 'Complete
              Payment' if this is not a duplicate.
            </div>
          </React.Fragment>
        }
      />
    </>
  );
};

export default UpdateSusbcribePlan;
